// src/firebase/itemsCollectionSync.js
//
// Keeps top-level `items`/`services` collections in sync with the embedded
// shops/{shopId}.items[]/.services[] arrays, which remain the source of
// truth. The mirror exists so Welcome-page-style queries can eventually
// filter/limit server-side (where/orderBy/limit against a real collection)
// instead of scanning every shop document — see getFeaturedItemsFromCollection/
// getFeaturedServicesFromCollection in firebaseService.js.
//
// Every write site that touches shops/{id}.items[]/.services[] should call
// the matching function here immediately after its own Firestore write
// succeeds. A mirror failure is logged and swallowed, never rethrown — the
// embedded array write already succeeded and is what the user-facing action
// actually depends on.
import { db } from './config';
import { doc, setDoc, deleteDoc, getDocs, collection, query, where, writeBatch } from 'firebase/firestore';

const BATCH_LIMIT = 500;

/** Builds the denormalized shop fields to persist onto every mirrored item/service doc — same field set getFeaturedItems/getFeaturedServices already compute at read time (firebaseService.js), just persisted at write time instead. */
export const buildShopFields = (shopId, shopData) => ({
  shopId,
  shopName: shopData?.name || 'Unknown Shop',
  shopUsername: shopData?.username || null,
  shopProfilePicture: shopData?.profile || null,
  shopDescription: shopData?.description || '',
  shopTheme: shopData?.theme || {},
  shopStatus: shopData?.accountStatus || 'active',
  accountStatus: shopData?.accountStatus || 'active',
  storeOpen: shopData?.storeOpen !== false,
  shopStoreType: shopData?.storeType || null,
  shopFollowersCount: (shopData?.followers || []).length,
  shopMasterAddresses: shopData?.masterAddresses || [],
  currency: shopData?.currency || 'USD',
  shopHideFromWelcomePage: shopData?.hideFromWelcomePage === true,
  shopGuestCheckoutFields: shopData?.guestCheckoutFields || null,
});

const mirrorDoc = (entity, shopId, shopFields) => ({
  ...entity,
  ...shopFields,
  shopId,
  // Normalized to a real boolean (embedded-array readers treat a missing/
  // undefined `visible` as visible too — matching that here is what lets
  // getFeaturedItemsFromCollection query `where('visible','==',true)`
  // server-side instead of the old array's implicit "not false" check.
  visible: entity?.visible !== false,
  updatedAt: new Date().toISOString(),
});

const mirrorFull = async (collectionName, shopId, currentEntities, shopFields, label) => {
  try {
    const validEntities = (currentEntities || []).filter(e => e && e.id);
    const currentIds = new Set(validEntities.map(e => e.id));

    const existingSnap = await getDocs(query(collection(db, collectionName), where('shopId', '==', shopId)));
    const staleIds = existingSnap.docs.map(d => d.id).filter(id => !currentIds.has(id));

    const ops = [
      ...validEntities.map(entity => ({ type: 'set', id: entity.id, data: mirrorDoc(entity, shopId, shopFields) })),
      ...staleIds.map(id => ({ type: 'delete', id })),
    ];

    for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
      const batch = writeBatch(db);
      ops.slice(i, i + BATCH_LIMIT).forEach(op => {
        const ref = doc(db, collectionName, op.id);
        if (op.type === 'set') batch.set(ref, op.data);
        else batch.delete(ref);
      });
      await batch.commit();
    }
  } catch (error) {
    console.warn(`⚠️ ${label} collection mirror sync failed (embedded array write already succeeded):`, error);
  }
};

const upsertMirrored = async (collectionName, entity, shopFields, label) => {
  if (!entity?.id || !shopFields?.shopId) return;
  try {
    await setDoc(doc(db, collectionName, entity.id), mirrorDoc(entity, shopFields.shopId, shopFields));
  } catch (error) {
    console.warn(`⚠️ ${label} mirror upsert failed:`, error);
  }
};

const deleteMirrored = async (collectionName, entityId, label) => {
  if (!entityId) return;
  try {
    await deleteDoc(doc(db, collectionName, entityId));
  } catch (error) {
    console.warn(`⚠️ ${label} mirror delete failed:`, error);
  }
};

/** Full-array-overwrite call sites: upserts every current item, deletes any mirrored item no longer present. */
export const mirrorItemsFull = (shopId, currentItems, shopFields) => mirrorFull('items', shopId, currentItems, shopFields, 'Item');
export const mirrorServicesFull = (shopId, currentServices, shopFields) => mirrorFull('services', shopId, currentServices, shopFields, 'Service');

/** Single-entity call sites (arrayUnion create, or a read-modify-write update of one element). */
export const upsertMirroredItem = (item, shopFields) => upsertMirrored('items', item, shopFields, 'Item');
export const upsertMirroredService = (service, shopFields) => upsertMirrored('services', service, shopFields, 'Service');

/** Single-entity delete call sites. */
export const deleteMirroredItem = (itemId) => deleteMirrored('items', itemId, 'Item');
export const deleteMirroredService = (serviceId) => deleteMirrored('services', serviceId, 'Service');
